import { format } from "date-fns";

import { numberFormat } from "@/utils/utils";
import {
  type RealEstateDocumentCategory,
  type RealEstateDocumentSection,
} from "../types";

/**
 * 文字列として渡されたyyyy-mm-ddからyyyy/MM/ddに変換する
 * @param d 日時(yyyy-mm-dd)
 * 成功した場合はyyyy/MM/ddの形式で返す
 * 失敗した場合は未設定を返す
 */
const formatDate = (d: string | undefined): string => {
  if (d) {
    const date = new Date(d);
    if (!Number.isNaN(date.getTime())) {
      return format(date, "yyyy/MM/dd");
    } else {
      console.error("Dateオブジェクトが正しく生成できませんでした", d);
    }
  }
  return "未設定";
};

const CHIBAN_PATTERN = [
  "甲",
  "乙",
  "丙",
  "丁",
  "戊",
  "子",
  "丑",
  "寅",
  "卯",
  "辰",
  "巳",
  "午",
  "未",
  "申",
  "酉",
  "戌",
  "亥",
  "イ",
  "ロ",
  "ハ",
  "ニ",
  "ホ",
  "ヘ",
  "ト",
  "チ",
  "リ",
  "ヌ",
  "ル",
  "ヲ",
  "ｲ",
  "ﾛ",
  "ﾊ",
  "ﾆ",
  "ﾎ",
  "ﾍ",
  "ﾄ",
  "ﾁ",
  "ﾘ",
  "ﾇ",
  "ﾙ",
  "ｦ",
];

/**
 * 種別の英語を日本語に変換する。
 * @param {string} value
 * @returns {string}
 */
const convertTypeEnglishToJapanese = (value: string): string => {
  if (value === "LAND") {
    return "土地";
  } else if (value === "BUILDING") {
    return "建物";
  } else if (value === "CONDOMINIUM") {
    return "区分建物";
  } else if (value === "ONE_BUILDING") {
    return "一棟";
  } else {
    return "";
  }
};

/**
 * 推計地積の値をフォーマット(少数第2位を切り捨て)
 * @param area 地積の値。nullの場合はデフォルト値として "—" を返す。
 * @return フォーマットされた推計地積の値。nullの場合は "—"。
 **/
const formatArea = (area: number | null): string => {
  return area ? numberFormat(area, { maximumFractionDigits: 2 }, "floor") : "—";
};

const CHIBAN_FULL_PATTERN = CHIBAN_PATTERN.join("|");

/**
 * 地番APIで取得した地番の中に不適切な文字列（道や無など）が含まれる場合はtrueを返す。
 * @param chiban 地番APIで取得した地番。
 * @return 不適切な文字列が含まれる場合はtrue、含まれない場合はfalse。
 **/
const skipSelectChiban = (chiban: string): boolean => {
  // 日本語の文字またはアルファベットを含む場合はスキップ（CHIBAN_FULL_PATTERNは除く）
  return (
    /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF a-zA-Z]/.test(chiban) &&
    !new RegExp(CHIBAN_FULL_PATTERN).test(chiban)
  );
};

const convertFullWidthDigitsAndHyphenToHalfWidth = (str: string): string => {
  return str.replace(/[０-９－]/g, (char) => {
    return String.fromCharCode(char.charCodeAt(0) - 0xfee0);
  });
};

/**
 * 全角数字を半角に変換する
 * @param str 変換対象の文字列
 * @returns 全角数字を半角に変換した文字列
 */
const convertFullWidthDigitsToHalfWidth = (str: string): string => {
  return str.replace(/[０-９]/g, (char) => {
    return String.fromCharCode(char.charCodeAt(0) - 0xfee0);
  });
};

/**
 * 所在（location）を正規化する
 * - 全角数字を半角に変換
 * - 余計な空白を削除
 * @param location 所在
 * @returns 正規化された所在
 */
const normalizeLocation = (location: string): string => {
  return convertFullWidthDigitsToHalfWidth(location).replace(/\s+/g, "").trim();
};

/**
 * 地番または家屋番号を正規化する
 * - 全角数字を半角に変換
 * - 各種ハイフン文字を半角ハイフン(-)に統一
 * - 余計な空白を削除
 * @param chibanKaokuNumber 地番または家屋番号
 * @returns 正規化された地番または家屋番号
 */
const normalizeChibanKaokuNumber = (chibanKaokuNumber: string): string => {
  return chibanKaokuNumber
    .replace(/[０-９]/g, (char) =>
      String.fromCharCode(char.charCodeAt(0) - 0xfee0)
    )
    .replace(/[－ー―‐‑–—−]/g, "-")
    .replace(/\s+/g, "")
    .trim();
};

// NOTE: PoCで実施する機能のため、DBから呼び出していない
const DOCUMENT_TYPES: Record<string, RealEstateDocumentSection> = {
  BOOK: {
    id: "book",
    label: "不動産登記関係",
    categories: [
      {
        id: "picture_land",
        label: "登記簿謄本（土地）",
        cost: 600,
        explanationUrl:
          "https://houmukyoku.moj.go.jp/homu/content/000130851.pdf",
        subCategories: [
          {
            id: "book_land_all_matter",
            label: "全部事項（登記事項）",
            cost: 600,
          },
          { id: "book_land_one_part", label: "一部事項", cost: 600 },
          { id: "book_land_owner_info", label: "所有者事項", cost: 600 },
        ],
      },
      {
        id: "picture_building",
        label: "登記簿謄本（建物）",
        cost: 600,
        explanationUrl:
          "https://houmukyoku.moj.go.jp/homu/content/000130851.pdf",
        subCategories: [
          {
            id: "book_building_all_matter",
            label: "全部事項（登記事項）",
            cost: 600,
          },
          { id: "book_building_one_part", label: "一部事項", cost: 600 },
          { id: "book_building_owner_info", label: "所有者事項", cost: 600 },
        ],
      },
      { id: "adjacent_place_summary", label: "隣地要約書", cost: 0 },
      {
        id: "closure_book",
        label: "閉鎖謄本",
        cost: 600,
        subCategories: [
          { id: "closure_book_genzai", label: "現在地番のみ", cost: 600 },
          {
            id: "closure_book_subdivision",
            label: "分筆前までさかのぼる",
            cost: 600,
          },
        ],
      },
      {
        id: "transfer_closure_book",
        label: "移記閉鎖謄本",
        cost: 600,
        subCategories: [
          {
            id: "transfer_closure_book_genzai",
            label: "現在地番のみ",
            cost: 600,
          },
          {
            id: "transfer_closure_book_subdivision",
            label: "分筆前までさかのぼる",
            cost: 600,
          },
        ],
      },
      {
        id: "old_land_ledger",
        label: "旧土地台帳",
        cost: 0,
        subCategories: [
          { id: "old_land_ledger_genzai", label: "現在地番のみ", cost: 0 },
          {
            id: "old_land_ledger_subdivision",
            label: "分筆前までさかのぼる",
            cost: 0,
          },
        ],
      },
    ] as RealEstateDocumentCategory[],
  },
  PICTURE: {
    id: "picture",
    label: "不動産図面関係",
    categories: [
      { id: "kozu", label: "公図", cost: 500 },
      { id: "chisekisokuryouzu", label: "地積測量図", cost: 500 },
      { id: "tatemonozumen", label: "建物図面", cost: 500 },
      {
        id: "old_kozu",
        label: "旧公図",
        cost: 500,
        subCategories: [
          { id: "old_kozu_genzai", label: "現在地番のみ", cost: 500 },
          {
            id: "old_kozu_subdivision",
            label: "分筆前までさかのぼる",
            cost: 500,
          },
        ],
      },
    ] as RealEstateDocumentCategory[],
  },
  URBAN_PLANNING: {
    id: "urban_planning",
    label: "都市計画・開発関係",
    categories: [
      { id: "urban_planning_map", label: "都市計画図", cost: 0 },
      { id: "development_register", label: "開発登録簿", cost: 0 },
      { id: "urban_planning_road_map", label: "都市計画道路線図", cost: 0 },
      { id: "replot_map", label: "換地図・仮換地図", cost: 0 },
    ] as RealEstateDocumentCategory[],
  },
  ROAD_INFRASTRUCTURE: {
    id: "road_infrastructure",
    label: "道路・インフラ関係",
    categories: [
      { id: "road_ledger", label: "道路台帳", cost: 0 },
      {
        id: "water_pipeline_map",
        label: "水道管管理図（上水道台帳）",
        cost: 0,
      },
      {
        id: "narrow_road_consultation",
        label: "狭隘協議書（指定道路調書）",
        cost: 0,
      },
      { id: "designated_road_map", label: "位置指定道路図", cost: 0 },
      { id: "road_category_map", label: "道路種別図", cost: 0 },
      { id: "cadastral_map", label: "境界確定図", cost: 0 },
      { id: "sewerage_ledger", label: "下水道台帳", cost: 0 },
      { id: "gas_pipeline_map", label: "ガス管管理図面", cost: 0 },
    ] as RealEstateDocumentCategory[],
  },
  BUILDING_ADMINISTRATIVE: {
    id: "building_administrative",
    label: "建築・行政関係",
    categories: [
      {
        id: "building_plan_summary",
        label: "建築計画概要書",
        tooltip:
          "対象地番や家屋番号上にある過去も含めたすべてのものを取得します",
        cost: 0,
        subCategories: [
          {
            id: "old_building_plan_summary_genzai",
            label: "対象地のみ",
            cost: 0,
          },
          {
            id: "old_building_plan_summary_subdivision",
            label: "対象地が存在しない場合は、隣接地も取得する",
            cost: 0,
          },
        ],
      },
      {
        id: "ledger_certificate_workpiece",
        label: "台帳記載事項証明書（工作物）",
        tooltip:
          "対象地番や家屋番号上にある過去も含めたすべてのものを取得します",
        cost: 0,
      },
      {
        id: "ledger_certificate_elevator",
        label: "台帳記載事項証明書（昇降機）",
        tooltip:
          "対象地番や家屋番号上にある過去も含めたすべてのものを取得します",
        cost: 0,
      },
      {
        id: "ledger_certificate_building",
        label: "台帳記載事項証明書（建物）",
        tooltip:
          "対象地番や家屋番号上にある過去も含めたすべてのものを取得します",
        cost: 0,
      },
      { id: "periodic_inspection", label: "定期検査報告書", cost: 0 },
    ] as RealEstateDocumentCategory[],
  },
  ENVIRONMENTAL: {
    id: "environmental",
    label: "環境・防災関係",
    categories: [
      {
        id: "water_pollution_facilities",
        label: "水質汚濁防止法の特定施設一覧",
        cost: 0,
      },
      { id: "sewerage_facilities", label: "下水道法の特定施設一覧", cost: 0 },
      { id: "hazard_map_landslide", label: "ハザードマップ（土砂）", cost: 0 },
      {
        id: "hazard_map_flood_pluvial",
        label: "ハザードマップ（浸水/内水）",
        cost: 0,
      },
      { id: "hazard_map_inundation", label: "ハザードマップ（洪水）", cost: 0 },
      { id: "hazard_map_high_tide", label: "ハザードマップ（高潮）", cost: 0 },
      { id: "hazard_map_earthquake", label: "ハザードマップ（地震）", cost: 0 },
      {
        id: "hazard_map_road_disaster_prevention",
        label: "ハザードマップ（道路防災）",
        cost: 0,
      },
      {
        id: "cultural_property_survey",
        label: "埋蔵文化財包蔵地図・調査書",
        cost: 0,
      },
    ] as RealEstateDocumentCategory[],
  },
  VALUATION: {
    id: "valuation",
    label: "評価関係",
    categories: [
      { id: "road_price_map", label: "路線価図", cost: 0 },
    ] as RealEstateDocumentCategory[],
  },
};

export { updatePropertyTags } from "./popup/tags/updatePropertyTags";
export { findExistingRow } from "./propertySelection/findExistingRow";
export { generateNextId } from "./propertySelection/generateNextId";
export { selectExistingRow } from "./propertySelection/selectExistingRow";

interface LatLng {
  lat: number;
  lng: number;
}

/**
 * 指定した座標のGoogle Maps衛星写真のURLを生成する。
 * ピンを立てつつ衛星写真モードで開く。
 * @param zoom Google Mapのズームレベル
 * @param coordinates 指定座標
 * @return {string} Google Mapの衛星写真のURLを返す、座標がundefinedの場合はGoogle Mapデフォルトの位置のURLを返す。
 */
const generateGoogleMapUrl = (zoom: number, coordinates?: LatLng): string => {
  const googleMapBaseURL = "https://www.google.com/maps/";

  if (coordinates != null) {
    // ピンを立てつつ衛星写真モードで開く t=k: 衛星写真モード, z: ズームレベル
    return `${googleMapBaseURL}?q=${coordinates.lat},${coordinates.lng}&t=k&z=${zoom}`;
  }

  // 座標がない場合はデフォルトの衛星写真モードで開く
  return `${googleMapBaseURL}?t=k`;
};

export {
  convertTypeEnglishToJapanese,
  formatArea,
  formatDate,
  skipSelectChiban,
  convertFullWidthDigitsAndHyphenToHalfWidth,
  normalizeLocation,
  normalizeChibanKaokuNumber,
  generateGoogleMapUrl,
  DOCUMENT_TYPES,
};
export { extractGeospaceBounds } from "./extractGeospaceBounds";
